<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />

   <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->


   <!-- The Modal -->
   <RecordFormModal
      :title="isEditing ? 'Update MFA & Location' : 'MFA & Location'"
      title-icon="bi bi-shield-lock"
      :is-editing="isEditing"
      :is-saving="isSaving"
      :alert="defaultAlert"
      :show-sample="false"
      @save="saveOrEditRecord"
      @reset="resetForm"
   >
      <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
      <HiddenField v-model="formInputs.id" />

      <!-- Whose settings these are -->
      <h5 v-if="formInputs.user_name" class="sa-core-color mt-1 mb-2">
         <i class="fa fa-user me-1 opacity-75"></i>{{ formInputs.user_name }}
      </h5>

      <!-- Row 1: MFA & Location -->
      <div class="row g-3 mb-0">
         <SelectField
            v-model="formInputs.mfa_enabled"
            col="col-md-6"
            name="mfa_enabled"
            label="Login code (MFA)"
            icon="fa fa-lock"
            :options="mfaOptions"
            :reduce="option => option.value"
            placeholder="Select..."
            hint="Enabled: a code is sent by SMS or email at every login."
            tooltip="Please choose Enabled or Disabled"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
         />
         <SelectField
            v-model="formInputs.is_locationRequired"
            col="col-md-6"
            name="is_locationRequired"
            label="Location required"
            icon="fa fa-map-marker"
            :options="locationOptions"
            :reduce="option => option.value"
            placeholder="Select..."
            hint="Enabled: the user must allow location to log in and work."
            tooltip="Please choose Enabled or Disabled"
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
         />
      </div>
   </RecordFormModal>

   
   <h4 class="sa-core-color fw-semibold mb-2">
      <i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}
   </h4>
   <!--Tables-->
   <div class="card glass mt-0 bg-sa-card">
      <div class="card-body bg-sa-card-body p-0">
                  <!-- Table action row (shared: TableToolbar.vue). No Add: users are created on the Users page -->
         <TableToolbar
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('mfa_and_location_settings.delete')"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
         >
            <!-- More menu -->
            <template #more>
               <li><h6 class="dropdown-header">Data</h6></li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                     <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                     <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                  </button>
               </li>
               <li>
                  <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                     <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                  </button>
               </li>
            </template>
         </TableToolbar>
         <!-- Table Search Panel -->

         <!-- Table Search Panel -->
         <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
         />
         <!-- What the table is currently showing -->
         <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
         />
         <!-- Table -->
         <div class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('first_name')" column="first_name" :table-data="tableData" @sort="orderBy">User</SortableTh>
         <SortableTh v-if="isColumnVisible('role')" column="role" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Role</SortableTh>
         <SortableTh v-if="isColumnVisible('mfa_enabled')" column="mfa_enabled" :table-data="tableData" @sort="orderBy" class="hide-sm-td">MFA</SortableTh>
         <SortableTh v-if="isColumnVisible('is_locationRequired')" column="is_locationRequired" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Location</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="[
                { 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) },
                record.pinned_role ? 'pinned-row pinned-' + record.pinned_role : ''
             ]"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('first_name')">
               <!-- Phones: + / − and the number go in front of the user (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
               <img v-if="record.file_path" :src="`${$baseUrl}/users/${record.file_path}`" height="24" width="24" class="rounded-circle me-1 object-fit-cover" />
               {{ getFullName(record) }}
               <span v-if="record.pinned_role === 'system_admin'" class="badge bg-warning text-dark ms-1">
                  <i class="fa fa-shield me-1"></i>System Admin
               </span>
               <span v-else-if="record.pinned_role === 'developer'" class="badge bg-info text-dark ms-1">
                  <i class="fa fa-code me-1"></i>Developer
               </span>
            </td>
            <td v-if="isColumnVisible('role')" class="hide-sm-td">{{ getRoleLabel(record.role) }}</td>
            <td v-if="isColumnVisible('mfa_enabled')" class="hide-sm-td">
               <span :class="isOn(record.mfa_enabled) ? 'badge bg-success' : 'badge bg-secondary'">
                  <i class="fa me-1" :class="isOn(record.mfa_enabled) ? 'fa-lock' : 'fa-unlock'"></i>
                  {{ isOn(record.mfa_enabled) ? 'On' : 'Off' }}
               </span>
            </td>
            <td v-if="isColumnVisible('is_locationRequired')" class="hide-sm-td">
               <span :class="isOn(record.is_locationRequired) ? 'badge bg-success' : 'badge bg-secondary'">
                  <i class="fa fa-map-marker me-1"></i>
                  {{ isOn(record.is_locationRequired) ? 'Required' : 'Not required' }}
               </span>
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  edit-label="Edit MFA & Location"
                  :can-soft-delete="$hasPermission('mfa_and_location_settings.delete') && !record.pinned_role"
                  :can-restore="$hasPermission('mfa_and_location_settings.delete')"
                  :can-delete-permanently="$hasPermission('mfa_and_location_settings.delete') && !record.pinned_role"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                  :modal-id="'deleteCrudModal' + record.id"
                  :title="deleteModalTitle"
                  :message="deleteModalMessage"
                  @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="6" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>User Number:</strong> {{ record.user_number }}</div>
                     <div><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.email }}</div>
                     <div><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone }}</div>
                     <div><i class="fa fa-id-badge"></i><strong>Username:</strong> {{ record.username }}</div>
                     <div><i class="fa fa-building"></i><strong>Home Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Home Branch:</strong> {{ record.branch_name || 'All Branches' }}</div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('first_name') }"><i class="fa fa-user"></i><strong>User:</strong> {{ getFullName(record) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('role') }"><i class="fa fa-users"></i><strong>Role:</strong> {{ getRoleLabel(record.role) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('mfa_enabled') }">
                        <i class="fa fa-lock"></i><strong>MFA:</strong>
                        <span :class="isOn(record.mfa_enabled) ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ isOn(record.mfa_enabled) ? 'On' : 'Off' }}
                        </span>
                     </div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_locationRequired') }">
                        <i class="fa fa-map-marker"></i><strong>Location:</strong>
                        <span :class="isOn(record.is_locationRequired) ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ isOn(record.is_locationRequired) ? 'Required' : 'Not required' }}
                        </span>
                     </div>

                     <!-- Always in details -->
                     <div>
                        <i class="fa fa-toggle-on"></i><strong>Account:</strong>
                        <span :class="isOn(record.is_active) ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ isOn(record.is_active) ? 'Active' : 'Inactive' }}
                        </span>
                     </div>
                     <div><i class="fa fa-user"></i><strong>Created By:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle (no Add: users are created on the Users page) -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="8" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No users yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Users added on the Users page appear here.'"
               icon="fa fa-shield"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="8">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>
         </div>
         <!-- Bottom Controls: Delete & Pagination -->
      </div>
   </div>
   <!--End Tables-->
</template>
<script>
import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import usersMfaAndLocationMixin from "./mixins/usersMfaAndLocationMixin.js";
import tableMixin from "@/mixins/tableMixin.js";
import axios from 'axios';


export default {
   name: 'UsersMfaAndLocation',
   mixins: [tableMixin, usersMfaAndLocationMixin], // shared table methods first, then this page's own
   components: {
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_mfa_and_location.php", // used by tableMixin's fetchRecords, delete, export
         exportFileName: "usersMfaAndLocationExport", // name of the exported file
         columnsKey: "usersmfaandlocation", // saves this page's hidden columns (Columns button)
         formInputs: this.getDefaultFormInputs(),
         rolesOptions: [],
         pageIcon: 'fa fa-shield', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "First Name", value: "first_name" },
            { label: "Middle Name", value: "middle_name" },
            { label: "Last Name", value: "last_name" },
            { label: "Email", value: "email" },
            { label: "Phone", value: "phone" },
            { label: "Username", value: "username" },
         ],
         mfaOptions: [
            { label: "Enabled", value: 1 },
            { label: "Disabled", value: 0 }
         ],
         locationOptions: [
            { label: "Enabled", value: 1 },
            { label: "Disabled", value: 0 }
         ]
      };
   },
   mounted(){
      this.fetchRolesOptions(); // role names for the table
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
      // Page heading that follows the chosen filter.
       pageTitle() {
           const titles = {
               all: "Users MFA & Location",
               byme: "Users MFA & Location By Me",
               deletedone: "Users MFA & Location Deleted Records",
           };
           return titles[this.tableData.recordFilter] || "Users MFA & Location";
       },
              // Columns the user can show or hide (Columns button)
       tableColumns() {
           return [
               { key: "first_name", label: "User" },
               { key: "role", label: "Role" },
               { key: "mfa_enabled", label: "MFA" },
               { key: "is_locationRequired", label: "Location" },
           ];
       },
   },
   methods: {
      getFullName(record) {
         return [record.first_name, record.middle_name, record.last_name]
            .filter(Boolean)
            .join(' ');
      },
      getRoleLabel(value) {
         const option = this.rolesOptions.find(o => Number(o.id) === Number(value));
         return option ? option.role : value;
      },
      isOn(value) {
         return Number(value) === 1;
      },
      fetchRolesOptions(){
         let url = `${this.apiUrl}?action=fetchRoles`;

         axios.get(url,this.$handleConfig())
            .then((response) => {
               if (response.data.status=="success") {
                  this.handleFetchRolesSuccess(response);
               } else {
                  this.handleAlert("starting",response.data.message,"danger","page");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","page");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","page");
                }
               
            });
      },
      // Selection bar: System Admin and Developer users (pinned_role) are never deleted,
      // same as the row menu. Take them out of the ticked rows, then run the shared bulk action.
      runBulkAction(deleteType = "soft") {
         if (deleteType === "soft") {
            const protectedIds = this.tableData.records
               .filter((record) => record.pinned_role)
               .map((record) => String(record.id));
            const kept = this.tableData.selectedRecordIds.filter((id) => !protectedIds.includes(String(id)));
            if (!kept.length) {
               this.handleAlert("ending", "System Admin and Developer users can't be deleted.", "warning", "page");
               return;
            }
            this.tableData.selectedRecordIds = kept;
         }
         return tableMixin.methods.runBulkAction.call(this, deleteType);
      },
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            user_name: "",          // shown at the top of the form (not sent)
            mfa_enabled: "",
            is_locationRequired: "",
         };
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'first_name',
            searchPlaceHolderText: 'Search by First Name',
         };
      },
      saveOrEditRecord() {    
      if (this.isSaving) return;
      this.isSaving = true;

      try {     
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;
         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) {
            this.$playErrorSound();
             this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
            this.isSaving = false;
            return; 
         }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
             this.tableData.currentPage = 1;
         }
         
         //To test performance
         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  this.handleSaveOrEditSuccess(response);
               } else {
                   const msg = response.data.message;
                   this.handleError("starting", Array.isArray(msg) ? msg[0] : msg, "danger", "modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
             .finally(() => {
                 this.isSaving = false;
             });} catch (error) {
             this.isSaving = false;
             throw error;
         }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append("mfa_enabled", this.formInputs.mfa_enabled);
         formData.append("is_locationRequired", this.formInputs.is_locationRequired);
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.isEditing = true;
         this.formInputs.id = records.id ?? '';
         this.formInputs.user_name = this.getFullName(records);
         // API returns "1"/"0" strings; options use numbers
         this.formInputs.mfa_enabled = records.mfa_enabled != null ? Number(records.mfa_enabled) : '';
         this.formInputs.is_locationRequired = records.is_locationRequired != null ? Number(records.is_locationRequired) : '';
         this.$clearValidationStates('#recordForm');
      },
   },
}; 
</script>